import { useCallback, useState } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';

/* ------------------------------------------------------------------ */
/*  useSendWhatsAppReminder (PRD.221)                                 */
/*                                                                    */
/*  Thin client for the `psendAdhocWhatsApp` Cloud Function. The      */
/*  backend owns phone resolution, opt-in checks, template            */
/*  resolution, provider sending, retries, and delivery persistence — */
/*  this hook only owns transient request state. Status is read back  */
/*  from the PowerSync notification_jobs mirror by the caller.        */
/* ------------------------------------------------------------------ */

const WHATSAPP_URL = import.meta.env.VITE_WHATSAPP_NOTIFICATIONS_URL;

const TEMPLATE_ID = 'APPOINTMENT_REMINDER';

export type SendReminderStatus = 'idle' | 'sending' | 'success' | 'error';

export interface SendReminderInput {
  patientId: string;
  /**
   * Appointment id — the backend builds the provider template parameters from
   * `notification_templates.metadata` + appointment context (PRD.254). The
   * client no longer controls the template variable order/shape.
   */
  appointmentId: string;
  /** Stable dedupe reference, e.g. "appointment:<id>:whatsapp-reminder". */
  clientReference: string;
}

/** Shape returned by psendAdhocWhatsApp (200 send-now / 202 scheduled). */
export interface SendReminderResult {
  jobId: string;
  deliveryId?: string;
  accepted?: boolean;
  providerMessageId?: string;
  scheduled?: boolean;
  retryScheduled?: boolean;
  errorMessage?: string;
}

export interface UseSendWhatsAppReminderReturn {
  status: SendReminderStatus;
  error: string | null;
  lastResult: SendReminderResult | null;
  /** True when the function endpoint is configured (VITE_WHATSAPP_NOTIFICATIONS_URL set). */
  enabled: boolean;
  /** Submit a reminder. Returns the result, or null on error. */
  send: (input: SendReminderInput) => Promise<SendReminderResult | null>;
  reset: () => void;
}

export function useSendWhatsAppReminder(): UseSendWhatsAppReminderReturn {
  const [status, setStatus] = useState<SendReminderStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SendReminderResult | null>(null);

  const enabled = Boolean(WHATSAPP_URL);

  const send = useCallback(async (input: SendReminderInput): Promise<SendReminderResult | null> => {
    if (!WHATSAPP_URL) {
      setError('WhatsApp notifications endpoint is not configured for this client.');
      setStatus('error');
      return null;
    }

    setStatus('sending');
    setError(null);

    try {
      const token = await getIdToken();
      const res = await fetch(WHATSAPP_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          templateId: TEMPLATE_ID,
          patientId: input.patientId,
          appointmentId: input.appointmentId,
          // No bodyParams — the backend derives them from template metadata.
          clientReference: input.clientReference,
        }),
      });

      const body: unknown = await res.json().catch(() => null);

      if (!res.ok) {
        setError(friendlyError(res.status, body));
        setStatus('error');
        return null;
      }

      const result = body as SendReminderResult;
      setLastResult(result);
      setStatus('success');
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send WhatsApp reminder.';
      setError(msg);
      setStatus('error');
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setLastResult(null);
  }, []);

  return { status, error, lastResult, enabled, send, reset };
}

/* ------------------------------------------------------------------ */
/*  Map backend status + error payloads to actionable UI copy.        */
/* ------------------------------------------------------------------ */

function friendlyError(httpStatus: number, body: unknown): string {
  const raw =
    typeof body === 'object' && body !== null && 'error' in body
      ? String((body as { error?: unknown }).error ?? '')
      : '';

  switch (httpStatus) {
    case 400:
      return raw || 'The reminder request was invalid. Check the patient phone number.';
    case 401:
      return 'Your session expired. Please sign in again.';
    case 403:
      return raw || 'This patient has not opted in to WhatsApp messaging.';
    case 404:
      return raw || 'The APPOINTMENT_REMINDER template is not configured for this clinic.';
    case 409:
      return raw || 'The APPOINTMENT_REMINDER template is inactive.';
    default:
      return raw || `Failed to send WhatsApp reminder (HTTP ${httpStatus}).`;
  }
}
